export type Period = 'monthly' | 'biweekly' | 'quarterly' | 'weekly';

function getTimezoneOffset(timezone: string): number {
  try {
    const now = new Date();
    const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC' });
    const tzStr = now.toLocaleString('en-US', { timeZone: timezone });
    return new Date(tzStr).getTime() - new Date(utcStr).getTime();
  } catch {
    return -5 * 60 * 60 * 1000;
  }
}

function nextBoundary(period: Period, dayOfMonth: number, dayOfWeek: number, nowLocal: Date): Date {
  const y = nowLocal.getFullYear();
  const m = nowLocal.getMonth();
  const d = nowLocal.getDate();

  if (period === 'monthly') {
    if (d < dayOfMonth) return new Date(y, m, dayOfMonth);
    const nextM = m === 11 ? 0 : m + 1;
    const nextY = m === 11 ? y + 1 : y;
    return new Date(nextY, nextM, dayOfMonth);
  }

  if (period === 'quarterly') {
    const currentQStart = Math.floor(m / 3) * 3;
    const nextQStart = currentQStart + 3 >= 12 ? 0 : currentQStart + 3;
    const nextQYear = currentQStart + 3 >= 12 ? y + 1 : y;
    if (m === currentQStart && d < dayOfMonth) return new Date(y, currentQStart, dayOfMonth);
    return new Date(nextQYear, nextQStart, dayOfMonth);
  }

  if (period === 'weekly') {
    const currentDow = nowLocal.getDay();
    const daysAhead = (dayOfWeek - currentDow + 7) % 7 || 7;
    return new Date(y, m, d + daysAhead);
  }

  // biweekly — next 14-day boundary anchored to first occurrence of dayOfWeek on/after 2024-01-01
  const MS_DAY = 24 * 60 * 60 * 1000;
  const EPOCH_BASE = new Date(2024, 0, 1);
  const epochDow = EPOCH_BASE.getDay();
  const daysToFirst = (dayOfWeek - epochDow + 7) % 7;
  const firstAnchor = new Date(2024, 0, 1 + daysToFirst);
  const MS14 = 14 * MS_DAY;
  const elapsed = Math.floor((nowLocal.getTime() - firstAnchor.getTime()) / MS14);
  return new Date(firstAnchor.getTime() + (elapsed + 1) * MS14);
}

export function getNextRunPreview(
  period: Period | null,
  dayOfMonth: number | null,
  dayOfWeek: number | null,
  timezone = 'UTC',
): string {
  if (!period) return 'Not configured';
  const tzOffset = getTimezoneOffset(timezone);
  const nowLocal = new Date(Date.now() + tzOffset);
  const boundary = nextBoundary(period, dayOfMonth ?? 1, dayOfWeek ?? 1, nowLocal);
  return boundary.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
